/**
 * optionsBuilder.service — client for the outlook-driven "Strategy Architect".
 * Backed by POST /v1/options/builder (ranked baskets) and
 *           POST /v1/options/builder/execute (persist chosen basket as PENDING).
 */
import api from './api';

export type OutlookMode = 'WONT_FALL_BELOW' | 'WONT_RISE_ABOVE' | 'WILL_BREACH' | 'STAY_IN_RANGE' | 'WILL_BREAK_ABOVE' | 'WILL_BREAK_BELOW';
export type Confidence = 'poor' | 'medium' | 'high';
export type Objective = 'balanced' | 'winrate' | 'ev' | 'rr';

export interface BuiltLeg {
  action: 'BUY' | 'SELL'; optionType: 'CE' | 'PE'; strike: number; expiry: string; symbol: string;
  quantity: number; ltp: number; bid: number | null; ask: number | null; bidAskSpread: number; costPerLeg: number;
}

export interface BuiltBasket {
  id: string; name: string; category: string;
  direction: 'LONG' | 'SHORT' | 'NEUTRAL';
  riskShape: 'debit' | 'credit' | 'undefined';
  fitScore: number; rationale: string;
  legs: BuiltLeg[];
  netPremium: number; marginEstimate: number;
  /** BROKER = real Zerodha SPAN basket margin; ESTIMATE = in-app heuristic. */
  marginSource?: 'BROKER' | 'ESTIMATE';
  maxProfit: number; maxLoss: number; breakEvens: number[];
  winProbability: number; expectedPnl: number; cvar5: number; roi: number;
  /** P&L if your view is proven at its hardest point (price merely holds the boundary). */
  provenPnl: number;
  /** Profitable when your analysis is correct (the strict filter keeps only these). */
  thesisOk?: boolean;
  greeks: { delta: number; gamma: number; theta: number; vega: number };
  reasoning: string[];
  warnings: string[]; fits: string[];
  payoffData: { spotRange: number[]; expiry: number[]; today: number[]; far?: number[] };
}

export interface BuilderRequest {
  underlyingSymbol: string;
  outlook: { mode: OutlookMode; level: number; level2?: number; horizonValue: number; horizonUnit: 'hours' | 'days'; confidence: Confidence };
  maxLegs?: number; topX?: number; objective?: Objective; includeUndefinedRisk?: boolean; availableMarginInr?: number;
}

export interface BuilderResponse {
  ok: boolean; reason?: string; notice?: string;
  underlying?: { symbol: string; feedSource: string; spot: number; expiry: string; atmStrike: number; strikeStep: number; ivRank: number; sigmaAnnual: number; tYears: number; lotMult: number; minStrike?: number; maxStrike?: number;
    optInvestmentAmountBuy?: number; optInvestmentAmountSell?: number; leverage?: number };
  baskets: BuiltBasket[]; consideredCount?: number;
}

export type TradingMode = 'PAPER' | 'LIVE';

export interface PlaceResult {
  ok: boolean; basketId?: number; status?: string; tradingMode?: string;
  reason?: string; gates?: Array<{ message: string }>;
}

export const optionsBuilderService = {
  async build(req: BuilderRequest): Promise<BuilderResponse> {
    try {
      const r = await api.post<{ success: boolean; data: BuilderResponse }>('/options/builder', req, { headers: { _suppressToast: 'true' } });
      return r.data?.data || { ok: false, reason: 'No response', baskets: [] };
    } catch (e: any) {
      return { ok: false, reason: e?.response?.data?.error?.message || e?.message || 'Builder failed', baskets: [] };
    }
  },
  async execute(underlyingSymbol: string, basket: BuiltBasket, lots = 1): Promise<{ ok: boolean; basketId?: number; reason?: string }> {
    try {
      const r = await api.post<{ success: boolean; data: { basketId: number } }>('/options/builder/execute', { underlyingSymbol, basket, lots });
      return { ok: !!r.data?.success, basketId: r.data?.data?.basketId };
    } catch (e: any) {
      return { ok: false, reason: e?.response?.data?.error?.message || e?.message || 'Execute failed' };
    }
  },
  /** Persist AND place the basket in one step. PAPER = simulated fills (no broker
   *  call); LIVE = real exchange orders via the existing gates. Both are monitored. */
  async place(underlyingSymbol: string, basket: BuiltBasket, lots: number, mode: TradingMode): Promise<PlaceResult> {
    try {
      const r = await api.post<{ success: boolean; data: { basketId: number; status: string; tradingMode: string } }>(
        '/options/builder/place', { underlyingSymbol, basket, lots, mode }, { headers: { _suppressToast: 'true' } });
      return { ok: !!r.data?.success, basketId: r.data?.data?.basketId, status: r.data?.data?.status, tradingMode: r.data?.data?.tradingMode };
    } catch (e: any) {
      const err = e?.response?.data?.error;
      return { ok: false, reason: err?.message || e?.message || 'Place failed', gates: err?.gates };
    }
  },
};
